// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { v4 as uuidv4 } from 'uuid';
import { createOrder } from '../../store/slices/orders';
import { getAffiliateProduct } from '../../store/slices/products';
import { getPriceFrequency, renderPriceFrequency, constructPrice } from '../../utils/helpers';
import api from '../../api';
import {
    resolveInitialTargetOrganisationId,
    shouldDisableCreateOrder,
    isDanskeBankOrganisation,
    getDefaultSelectedProductIds,
    filterCreateOrderProductOptions,
    filterCreateOrderProductsById,
    requiresCreateOrderFileUpload,
    hasDanskeBankLoanValidationProduct,
    buildCreateOrderCustomer,
    getCreateOrderAdvisorStorageKey,
    getCreateOrderAdvisorId,
    getCreateOrderAdvisorName,
    buildCreateOrderAssignedAdvisor,
} from './createOrderDialog.helpers';
import ProductOptionsSelect from './ProductOptionsSelect';
import { useAccessProductOptions } from './hooks/useAccessProductOptions';
import UploadFileModal from './UploadFileModal';

const useStyles = makeStyles(() => ({
    formControl: {
        marginTop: 20,
        minWidth: 120,
    },
}));

const getInitialCustomer = (customerInput) => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cprNr: '',
    bankAssociation: '',
    ...(customerInput || {}),
    partner: {
        email: '',
        cprNr: '',
        phone: '',
        ...(customerInput?.partner || customerInput?.household?.partner || {}),
    },
});

const getProfilePartnerEmail = (profileUser) => profileUser?.data?.household?.partner?.email || '';

const CreateOrderDialog = ({ open, onClose, onAccept, isLaw, customerInput }) => {
    const styles = useStyles();
    const dispatch = useDispatch();
    const { organisationId } = useParams();
    const navigate = useNavigate();
    const user = useSelector(state => state.auth.user);

    const [availableOrganisations, setAvailableOrganisations] = useState([]);
    const [organisationsLoading, setOrganisationsLoading] = useState(false);
    const [organisationsError, setOrganisationsError] = useState('');
    const [targetOrganisationId, setTargetOrganisationId] = useState(organisationId);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [shouldApplyOrganisationDefaultProduct, setShouldApplyOrganisationDefaultProduct] = useState(false);
    const [uploadedRequiredFile, setUploadedRequiredFile] = useState();
    const [targetOrganisationUsers, setTargetOrganisationUsers] = useState([]);
    const [targetOrganisationUsersLoading, setTargetOrganisationUsersLoading] = useState(false);
    const [targetOrganisationUsersError, setTargetOrganisationUsersError] = useState('');
    const [selectedAdvisorId, setSelectedAdvisorId] = useState('');

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [customer, setCustomer] = useState(getInitialCustomer(customerInput));
    const [error, setError] = useState({ email: '', products: '', file: '' });
    const affiliateId = isLaw ? 'jura-nord-advokater-aps' : 'penly';
    const hasCustomerUserId = !!customerInput?.userId;

    useEffect(() => {
        if (!targetOrganisationId) return;
        dispatch(getAffiliateProduct(targetOrganisationId, affiliateId));
    }, [dispatch, affiliateId, targetOrganisationId]);

    useEffect(() => {
        if (!open) return;

        let isMounted = true;

        const loadOrganisations = async () => {
            setOrganisationsLoading(true);
            setOrganisationsError('');

            try {
                const organisations = await api.teal.getOrderOrganisations();
                if (!isMounted) return;

                setAvailableOrganisations(organisations || []);

                setTargetOrganisationId((currentTargetOrganisationId) => resolveInitialTargetOrganisationId({
                    organisationId,
                    availableOrganisations: organisations,
                    currentTargetOrganisationId,
                }));
            } catch (e) {
                if (!isMounted) return;
                setOrganisationsError('Kunne ikke hente organisationer. Prøv igen.');
                setAvailableOrganisations([]);
                setTargetOrganisationId(undefined);
            } finally {
                if (isMounted) {
                    setOrganisationsLoading(false);
                }
            }
        };

        loadOrganisations();

        return () => {
            isMounted = false;
        };
    }, [open, organisationId]);

    useEffect(() => {
        if (!open || !targetOrganisationId) return;

        let isMounted = true;

        const loadTargetOrganisationUsers = async () => {
            setTargetOrganisationUsersLoading(true);
            setTargetOrganisationUsersError('');
            setSelectedAdvisorId('');

            try {
                const users = await api.teal.getUsersInOrganisation(targetOrganisationId);
                if (!isMounted) return;

                const resolvedUsers = users || [];
                const storedAdvisorId = localStorage.getItem(getCreateOrderAdvisorStorageKey(targetOrganisationId)) || '';
                const hasStoredAdvisor = resolvedUsers.some((advisor) => getCreateOrderAdvisorId(advisor) === storedAdvisorId);

                setTargetOrganisationUsers(resolvedUsers);
                setSelectedAdvisorId(hasStoredAdvisor ? storedAdvisorId : '');
            } catch (e) {
                if (!isMounted) return;
                setTargetOrganisationUsers([]);
                setSelectedAdvisorId('');
                setTargetOrganisationUsersError('Kunne ikke hente rådgivere.');
            } finally {
                if (isMounted) {
                    setTargetOrganisationUsersLoading(false);
                }
            }
        };

        loadTargetOrganisationUsers();

        return () => {
            isMounted = false;
        };
    }, [open, targetOrganisationId]);

    useEffect(() => {
        if (!open || !customerInput?.userId) return;

        let isMounted = true;

        const autofillPartnerEmailFromProfile = async () => {
            try {
                const profileUser = await api.penly.getUser(organisationId, customerInput.userId);
                if (!isMounted) return;

                const partnerEmail = getProfilePartnerEmail(profileUser);
                if (!partnerEmail) return;

                setCustomer((currentCustomer) => {
                    if (currentCustomer?.partner?.email) return currentCustomer;

                    return {
                        ...currentCustomer,
                        partner: {
                            ...(currentCustomer.partner || {}),
                            email: partnerEmail,
                        },
                    };
                });
            } catch (e) {
                // Partner email is optional, so profile lookup failures should not block order creation.
            }
        };

        autofillPartnerEmailFromProfile();

        return () => {
            isMounted = false;
        };
    }, [open, organisationId, customerInput?.userId]);

    const {
        lawProductOptions: rawLawProductOptions,
        nonLawProductOptions: rawNonLawProductOptions,
        productsById: rawProductsById,
    } = useAccessProductOptions(targetOrganisationId, isLaw);
    const lawProductOptions = useMemo(
        () => filterCreateOrderProductOptions({ productOptions: rawLawProductOptions, hasUserId: hasCustomerUserId }),
        [rawLawProductOptions, hasCustomerUserId],
    );
    const nonLawProductOptions = useMemo(
        () => filterCreateOrderProductOptions({ productOptions: rawNonLawProductOptions, hasUserId: hasCustomerUserId }),
        [rawNonLawProductOptions, hasCustomerUserId],
    );
    const productOptions = useMemo(() => [...lawProductOptions, ...nonLawProductOptions], [lawProductOptions, nonLawProductOptions]);
    const productsById = useMemo(
        () => filterCreateOrderProductsById({ productsById: rawProductsById, hasUserId: hasCustomerUserId }),
        [rawProductsById, hasCustomerUserId],
    );
    const selectedOrganisation = availableOrganisations.find(({ organisationId: availableOrganisationId }) => availableOrganisationId === targetOrganisationId);
    const isDanskeBankSelected = isDanskeBankOrganisation(selectedOrganisation || targetOrganisationId);
    const selectedProductIds = selectedProducts.map(({ product }) => product.id);
    const hasDanskeBankLoanValidation = hasDanskeBankLoanValidationProduct({ selectedProductIds });
    const canUploadDanskeBankFile = isDanskeBankSelected && hasCustomerUserId && hasDanskeBankLoanValidation;
    const requiresRequiredFileUpload = canUploadDanskeBankFile && requiresCreateOrderFileUpload();

    useEffect(() => {
        if (!open) return;
        setShouldApplyOrganisationDefaultProduct(true);
    }, [open, targetOrganisationId]);

    useEffect(() => {
        if (!open || !shouldApplyOrganisationDefaultProduct) return;

        const canIdentifyDanskeBankFromId = isDanskeBankOrganisation(targetOrganisationId);
        if (!selectedOrganisation && !canIdentifyDanskeBankFromId && organisationsLoading) return;

        const defaultSelectedIds = getDefaultSelectedProductIds({
            targetOrganisation: selectedOrganisation || targetOrganisationId,
            productOptions,
        });

        if (!defaultSelectedIds.length) {
            setShouldApplyOrganisationDefaultProduct(false);
            return;
        }

        const updatedProducts = defaultSelectedIds.map((id) => {
            const product = productsById[id] || {
                id,
                name: productOptions.find((option) => option.value === id)?.name || id,
                price: {},
            };
            const defaultPrice = product.price.hourly
                ? product.price.standard
                : product.price.now || product.price.standard || product.price.yearly?.now || product.price.yearly?.standard || product.price.monthly?.standard;
            const priceFrequency = getPriceFrequency(product.price);
            return { product, price: defaultPrice || 0, priceFrequency };
        });

        if (!updatedProducts.every(({ product }) => product?.name)) {
            return;
        }

        setSelectedProducts(updatedProducts);
        setError((prev) => ({ ...prev, products: '' }));
        setShouldApplyOrganisationDefaultProduct(false);
    }, [open, shouldApplyOrganisationDefaultProduct, selectedOrganisation, targetOrganisationId, productOptions, productsById, organisationsLoading]);

    const resetState = () => {
        setSelectedProducts([]);
        setCustomer(getInitialCustomer(customerInput));
        setError({ email: '', products: '', file: '' });
        setUploadModalOpen(false);
        setUploadedRequiredFile(undefined);
        setSelectedAdvisorId('');
        setTargetOrganisationUsers([]);
        setTargetOrganisationUsersError('');
        setTargetOrganisationUsersLoading(false);
        setShouldApplyOrganisationDefaultProduct(false);
    };

    const handleProductChange = (event) => {
        const selectedIds = event.target.value;
        const updatedProducts = selectedIds.map(id => {
            const existingProduct = selectedProducts.find(product => product.product.id === id);
            if (existingProduct) {
                return existingProduct;
            }
            const product = productsById[id] || {
                id,
                name: productOptions.find((option) => option.value === id)?.name || id,
                price: {},
            };
            const defaultPrice = product.price.hourly
                ? product.price.standard
                : product.price.now || product.price.standard || product.price.yearly?.now || product.price.yearly?.standard || product.price.monthly?.standard;
            const priceFrequency = getPriceFrequency(product.price);
            return { product, price: defaultPrice || 0, priceFrequency };
        });
        setSelectedProducts(updatedProducts);

        if (!requiresCreateOrderFileUpload()) {
            setError((prev) => ({ ...prev, file: '' }));
        }
    };

    const handlePriceChange = (index) => (event) => {
        const updatedProducts = [...selectedProducts];
        updatedProducts[index].price = parseFloat(event.target.value) || 0;
        setSelectedProducts(updatedProducts);
    };

    const handleCustomerChange = (field) => (event) => {
        setCustomer({ ...customer, [field]: event.target.value });
    };

    const handleCustomerPartnerChange = (field) => (event) => {
        setCustomer({
            ...customer,
            partner: {
                ...(customer.partner || {}),
                [field]: event.target.value,
            },
        });
    };

    const handleTargetOrganisationChange = (event) => {
        setTargetOrganisationId(event.target.value);
        setSelectedProducts([]);
        setSelectedAdvisorId('');
        setUploadModalOpen(false);
        setUploadedRequiredFile(undefined);
        setError((prev) => ({ ...prev, products: '', file: '' }));
    };

    const handleAdvisorChange = (event) => {
        const advisorId = event.target.value;
        setSelectedAdvisorId(advisorId);

        const storageKey = getCreateOrderAdvisorStorageKey(targetOrganisationId);
        if (advisorId) {
            localStorage.setItem(storageKey, advisorId);
        } else {
            localStorage.removeItem(storageKey);
        }
    };

    const handleUploadModalClose = (uploadedFile) => {
        setUploadModalOpen(false);

        if (uploadedFile) {
            setUploadedRequiredFile(uploadedFile);
            setError((prev) => ({ ...prev, file: '' }));
        }
    };

    const handleAccept = async () => {
        let hasError = false;
        const newError = { email: '', products: '', file: '' };

        if (!hasDanskeBankLoanValidation && !customer.email) {
            newError.email = 'Email skal udfyldes.';
            hasError = true;
        }

        if (selectedProducts.length === 0) {
            newError.products = 'Du skal vælge mindst ét produkt.';
            hasError = true;
        }

        if (requiresRequiredFileUpload && !uploadedRequiredFile) {
            newError.file = 'Du skal uploade en fil for Danske bank lånvalidering.';
            hasError = true;
        }

        setError(newError);

        if (!hasError) {
            const assignedTo = buildCreateOrderAssignedAdvisor(
                targetOrganisationUsers.find((advisor) => getCreateOrderAdvisorId(advisor) === selectedAdvisorId),
            );
            const orderToCreate = {
                items: selectedProducts.map(({ product, price, priceFrequency }) => ({
                    product: {
                        ...product,
                        price: constructPrice(price, priceFrequency),
                    },
                })),
                customer: buildCreateOrderCustomer(customer),
                createdBy: { userId: user.userId, name: user.fullName },
                vendor: targetOrganisationId,
                targetOrganisationId,
                calendlyBookingId: uuidv4(),
                disableCalendlyLookup: true,
                assignedTo,
                assignedAdvisor: assignedTo ? { userId: assignedTo.userId, name: assignedTo.name } : null,
            };
            const res = await dispatch(createOrder(organisationId, orderToCreate));
            if (res.isError) {
                // eslint-disable-next-line no-alert
                alert('Kunne ikke oprette ordre');
            } else {
                if (canUploadDanskeBankFile && uploadedRequiredFile) {
                    try {
                        await api.penly.uploadOrderFile(organisationId, customer.userId, res.orderId, uploadedRequiredFile);
                    } catch (uploadError) {
                        // eslint-disable-next-line no-alert
                        alert('Ordren blev oprettet, men filen kunne ikke uploades til ordren. Prøv at uploade filen igen fra ordren.');
                        return;
                    }
                }

                if (onAccept) {
                    onAccept();
                }
                navigate(`/${targetOrganisationId}/access/orders/${res.orderId}`);
                resetState();
            }
        }
    };

    return (
        <>
            <Dialog
                open={ open }
                onClose={ onClose }
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{ sx: { width: '660px', maxWidth: '96vw' } }}
            >
                <DialogTitle id="alert-dialog-title">Opret ny ordre</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">Vælg produkter, rediger priser og indtast kundeoplysninger for den nye ordre</DialogContentText>

                    <FormControl className={ styles.formControl } fullWidth error={ !!organisationsError }>
                        <InputLabel id="organisation-select-label">Organisation</InputLabel>
                        <Select
                            labelId="organisation-select-label"
                            value={ targetOrganisationId || '' }
                            onChange={ handleTargetOrganisationChange }
                            input={ <OutlinedInput label="Organisation" /> }
                            disabled={ organisationsLoading || availableOrganisations.length === 0 }
                        >
                            { availableOrganisations.map(({ organisationId, name }) => (
                                <MenuItem key={ organisationId } value={ organisationId }>{ name }</MenuItem>
                            )) }
                        </Select>
                        { organisationsLoading && <Typography variant="caption">Henter organisationer...</Typography> }
                        { organisationsError && <Typography color="error" variant="caption">{ organisationsError }</Typography> }
                    </FormControl>

                    <FormControl className={ styles.formControl } fullWidth error={ !!targetOrganisationUsersError }>
                        <InputLabel id="advisor-select-label">Rådgiver</InputLabel>
                        <Select
                            labelId="advisor-select-label"
                            value={ selectedAdvisorId }
                            onChange={ handleAdvisorChange }
                            input={ <OutlinedInput label="Rådgiver" /> }
                            disabled={ !targetOrganisationId || targetOrganisationUsersLoading || targetOrganisationUsers.length === 0 }
                        >
                            <MenuItem value="">Ingen</MenuItem>
                            { targetOrganisationUsers.map((advisor) => {
                                const advisorId = getCreateOrderAdvisorId(advisor);
                                const advisorName = getCreateOrderAdvisorName(advisor);
                                return (
                                    <MenuItem key={ advisorId || advisor.email } value={ advisorId }>
                                        { advisorName || advisor.email || advisorId }
                                    </MenuItem>
                                );
                            }) }
                        </Select>
                        { targetOrganisationUsersLoading && <Typography variant="caption">Henter rådgivere...</Typography> }
                        { targetOrganisationUsersError && <Typography color="error" variant="caption">{ targetOrganisationUsersError }</Typography> }
                    </FormControl>

                    <Card variant="outlined" sx={{ marginBottom: 2, marginTop: 2, borderColor: '#009094' }}>
                        <CardContent sx={{ paddingBottom: '12px !important' }}>
                            <Typography gutterBottom sx={{ color: '#009094', fontSize: 12, margin:0 }}>
                                Kunde
                            </Typography>
                            <Grid container spacing={ 2 }>
                                <Grid item xs={ 6 }>
                                    <TextField
                                        label="Fornavn"
                                        value={ customer.firstName }
                                        onChange={ handleCustomerChange('firstName') }
                                        fullWidth
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={ 6 }>
                                    <TextField
                                        label="Efternavn"
                                        value={ customer.lastName }
                                        onChange={ handleCustomerChange('lastName') }
                                        fullWidth
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                            <TextField
                                label="Kundens email"
                                value={ customer.email }
                                onChange={ handleCustomerChange('email') }
                                fullWidth
                                margin="normal"
                                error={ !!error.email }
                                helperText={ error.email }
                            />
                            { hasDanskeBankLoanValidation && (
                                <>
                                    <TextField
                                        label="Banktilknytning"
                                        value={ customer.bankAssociation || '' }
                                        onChange={ handleCustomerChange('bankAssociation') }
                                        fullWidth
                                        margin="normal"
                                    />
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 6 }>
                                            <TextField
                                                label="CPR-nr."
                                                value={ customer.cprNr || '' }
                                                onChange={ handleCustomerChange('cprNr') }
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={ 6 }>
                                            <TextField
                                                label="Telefonnummer"
                                                value={ customer.phone || '' }
                                                onChange={ handleCustomerChange('phone') }
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                    </Grid>
                                    <Typography gutterBottom sx={{ color: '#009094', fontSize: 12, marginTop: 2 }}>
                                        Partner
                                    </Typography>
                                    <TextField
                                        label="Partners email"
                                        value={ customer.partner?.email || '' }
                                        onChange={ handleCustomerPartnerChange('email') }
                                        fullWidth
                                        margin="normal"
                                    />
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 6 }>
                                            <TextField
                                                label="Partners CPR-nr."
                                                value={ customer.partner?.cprNr || '' }
                                                onChange={ handleCustomerPartnerChange('cprNr') }
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                        <Grid item xs={ 6 }>
                                            <TextField
                                                label="Partners telefonnummer"
                                                value={ customer.partner?.phone || '' }
                                                onChange={ handleCustomerPartnerChange('phone') }
                                                fullWidth
                                                margin="normal"
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) }
                        </CardContent>
                    </Card>

                    { canUploadDanskeBankFile && (
                        <Card variant="outlined" sx={{ marginBottom: 2, borderColor: error.file ? 'error.main' : '#009094' }}>
                            <CardContent sx={{ paddingBottom: '12px !important' }}>
                                <Typography gutterBottom sx={{ color: '#009094', fontSize: 12, margin:0 }}>
                                    Danske Bank filer
                                </Typography>
                                <Typography variant="body2">
                                    Vælg eventuelt en fil til ordren. Filen deles kun med rådgivere og gemmes ikke på kundens profil.
                                </Typography>
                                { uploadedRequiredFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Uploadet fil: { uploadedRequiredFile.fileName || uploadedRequiredFile.filename || uploadedRequiredFile.name }
                                    </Typography>
                                ) }
                                <Button sx={{ mt: 2 }} variant="outlined" onClick={ () => setUploadModalOpen(true) }>
                                    { uploadedRequiredFile ? 'Erstat fil' : 'Upload fil' }
                                </Button>
                                { error.file && (
                                    <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        { error.file }
                                    </Typography>
                                ) }
                            </CardContent>
                        </Card>
                    ) }

                    <div className={ styles.formControl }>
                        <ProductOptionsSelect
                            labelId="multi-select-label"
                            label="Vælg produkter"
                            multiple
                            value={ selectedProducts.map(({ product }) => product.id) }
                            onChange={ handleProductChange }
                            lawProductOptions={ lawProductOptions }
                            nonLawProductOptions={ nonLawProductOptions }
                            menuProps={{ anchorOrigin: { vertical: 'top', horizontal: 'left' }, transformOrigin: { vertical: 'bottom', horizontal: 'left' } }}
                            fullWidth
                            error={ !!error.products }
                        />
                        { error.products && <Typography color="error" variant="caption">{ error.products }</Typography> }
                    </div>
                    { selectedProducts.map((item, index) => (
                        <Grid container spacing={ 2 } key={ index } alignItems="center">
                            <Grid item xs={ 4 }>
                                <TextField
                                    label="Produkt"
                                    value={ item.product.name }
                                    disabled
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={ 4 }>
                                <TextField
                                    label="Pris"
                                    type="number"
                                    value={ item.price }
                                    onChange={ handlePriceChange(index) }
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                            <Grid item xs={ 4 }>
                                <TextField
                                    label="Pris frekvens"
                                    value={ renderPriceFrequency(item.priceFrequency) }
                                    disabled
                                    fullWidth
                                    margin="normal"
                                />
                            </Grid>
                        </Grid>
                    )) }
                </DialogContent>
                <DialogActions>
                    <Button onClick={ onClose } color="warning">
                        Fortryd
                    </Button>
                    <Button
                        onClick={ handleAccept }
                        autoFocus
                        color="primary"
                        variant="contained"
                        disabled={ shouldDisableCreateOrder({ organisationsLoading, organisationsError, targetOrganisationId }) }
                    >
                        Opret
                    </Button>
                </DialogActions>
            </Dialog>
            <UploadFileModal
                open={ uploadModalOpen }
                handleClose={ handleUploadModalClose }
                userId={ customerInput?.userId }
                settings={{ shouldShare: false }}
                deferUpload
                defaultFileType="Advice"
            />
        </>
    );
};

export default CreateOrderDialog;
